"use client";

import { c as _c } from "react/compiler-runtime";
import { useBreakpoint } from "@dc/hooks";
import { type ComponentProps, cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";
import { Drawer, DrawerBody, DrawerContent } from "../Drawer";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Tooltip } from "../Tooltip";

/**
 * A responsive container that renders a Popover on desktop (≥ sm breakpoint)
 * and a right-side Drawer on mobile, both driven by a single isOpen/onOpenChange state.
 *
 * The trigger element is wrapped in a PopoverTrigger on desktop. On mobile it receives
 * an onPress handler via cloneElement so it opens the Drawer when pressed.
 *
 * @example
 * <PopoverDrawer
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   tooltipLabel="Sort"
 *   trigger={<Button isIconOnly size="sm"><SortIcon /></Button>}
 *   desktopContent={<DesktopMenu />}
 *   mobileContent={<MobileMenu />}
 * />
 */
export interface PopoverDrawerProps {
  /** Controlled open state shared by both the Popover and the Drawer. */
  isOpen: boolean;
  /** Called with `true` when opening and `false` when closing. */
  onOpenChange: (open: boolean) => void;
  /**
   * The icon button that triggers the menu. On desktop it is wrapped in a
   * PopoverTrigger; on mobile it receives onPress via cloneElement.
   */
  trigger: ReactNode;
  /** Text shown in the Tooltip that wraps the trigger. */
  tooltipLabel: string;
  /** Defaults to the app's dark tooltip; override only where a surface needs the light one. */
  tooltipColor?: ComponentProps<typeof Tooltip>["color"];
  /** Content rendered inside the Popover on desktop. */
  desktopContent: ReactNode;
  /** Content rendered inside the Drawer on mobile. Should include any title/header. */
  mobileContent: ReactNode;
  /** Optional className forwarded to PopoverContent. */
  popoverClassName?: string;
}
export const PopoverDrawer = t0 => {
  const $ = _c(27);
  const {
    isOpen,
    onOpenChange,
    trigger,
    tooltipLabel,
    tooltipColor: t1,
    desktopContent,
    mobileContent,
    popoverClassName
  } = t0;
  const tooltipColor = t1 === undefined ? "foreground" : t1;
  const isDesktop = useBreakpoint("sm");
  const isDesktopPopoverOpen = isOpen && isDesktop;
  const isMobileDrawerOpen = isOpen && !isDesktop;
  let t2;
  if ($[0] !== onOpenChange || $[1] !== trigger) {
    t2 = isValidElement(trigger) ? cloneElement(trigger as ReactElement<{
      onPress?: () => void;
    }>, {
      onPress: () => onOpenChange(true)
    }) : trigger;
    $[0] = onOpenChange;
    $[1] = trigger;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const mobileTrigger = t2;
  let t3;
  if ($[3] !== desktopContent || $[4] !== isDesktop || $[5] !== isDesktopPopoverOpen || $[6] !== mobileTrigger || $[7] !== onOpenChange || $[8] !== popoverClassName || $[9] !== trigger) {
    t3 = isDesktop ? <Popover isOpen={isDesktopPopoverOpen} onOpenChange={onOpenChange} placement="bottom-start" offset={8}><PopoverTrigger>{trigger}</PopoverTrigger><PopoverContent className={popoverClassName}>{desktopContent}</PopoverContent></Popover> : mobileTrigger;
    $[3] = desktopContent;
    $[4] = isDesktop;
    $[5] = isDesktopPopoverOpen;
    $[6] = mobileTrigger;
    $[7] = onOpenChange;
    $[8] = popoverClassName;
    $[9] = trigger;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <span className="inline-flex">{t3}</span>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4 || $[14] !== tooltipColor || $[15] !== tooltipLabel) {
    t5 = <Tooltip content={tooltipLabel} color={tooltipColor}>{t4}</Tooltip>;
    $[13] = t4;
    $[14] = tooltipColor;
    $[15] = tooltipLabel;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      backdrop: "backdrop-blur-none bg-overlay-0"
    };
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== mobileContent) {
    t7 = <DrawerContent className="max-w-full p-0 sm:max-w-[22.5rem]"><DrawerBody>{mobileContent}</DrawerBody></DrawerContent>;
    $[18] = mobileContent;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== isMobileDrawerOpen || $[21] !== onOpenChange || $[22] !== t7) {
    t8 = <Drawer isOpen={isMobileDrawerOpen} onOpenChange={onOpenChange} placement="right" size="sm" classNames={t6}>{t7}</Drawer>;
    $[20] = isMobileDrawerOpen;
    $[21] = onOpenChange;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t5 || $[25] !== t8) {
    t9 = <>{t5}{t8}</>;
    $[24] = t5;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};